import { Directive, Host, NgZone, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DatePicker } from "primeng/datepicker";
import { Subscription } from "rxjs";

/**
 * Accessibility fix for the PrimeNG date picker calendar
 * (WCAG 4.1.2 Name, Role, Value / 1.3.1 Info and Relationships / 2.1.1 Keyboard).
 *
 * In the day view PrimeNG makes the inner day `<span>` the focusable cell
 * (it receives tabindex and `.focus()` during arrow-key navigation), but that
 * span's only accessible name is its text content — the day-of-month NUMBER.
 * The full date lives on the parent `<td [attr.aria-label]="date.day">`, which
 * is ALSO just the number and is not the focused element. As a result, a screen
 * reader user navigating the calendar hears only "15", never the month, year or
 * weekday.
 *
 * This directive rewrites each focusable day span's `aria-label` to the full,
 * localized date (e.g. "Wednesday, July 15, 2026") so navigation announces the
 * complete date and weekday. It reads PrimeNG's own `data-date` marker
 * (`"YYYY-M-D"`, month is 0-based) to avoid re-deriving the calendar maths.
 *
 * It relabels when the overlay opens and whenever the visible month/year
 * changes, and observes the panel so cells re-rendered by keyboard month
 * navigation are relabelled too. Opt-in via the attribute so it only affects
 * date pickers that request it.
 *
 * KEYBOARD NAVIGATION (WCAG 2.1.1): PrimeNG wires arrow-key navigation on each
 * day span, but only once a day cell HOLDS focus. When the calendar is opened
 * from the input/trigger button (the app uses `showIcon` + `[readonlyInput]`),
 * PrimeNG's `showOverlay()` sets `preventFocus = true`, so `initFocusableCell()`
 * gives the current day cell `tabindex="0"` but SKIPS `cell.focus()` — focus
 * stays on the trigger and pressing an arrow key does nothing (the calendar
 * "does not support keyboard navigation"). The ARIA APG date-picker-dialog
 * pattern expects focus to move into the grid onto the selected/today date when
 * it opens. This directive therefore moves focus onto that focusable day cell on
 * open so arrow-key navigation is immediately available.
 */
@Directive({
  standalone: false,
  selector: "p-datepicker[espDatepickerDayAriaLabels]"
})
export class DatepickerDayAriaLabelsDirective implements OnInit, OnDestroy {
  // PrimeNG's own initFocusableCell() cell-selection order for the day view:
  // the selected day, else today, else the first selectable day.
  private static readonly FOCUSABLE_DAY_SELECTORS = [
    ".p-datepicker-calendar td span.p-highlight:not(.p-disabled):not(.p-ink)",
    "td.p-datepicker-today span:not(.p-disabled):not(.p-ink)",
    ".p-datepicker-calendar td span:not(.p-disabled):not(.p-ink)"
  ];

  private readonly subscriptions = new Subscription();
  private observer: MutationObserver | null = null;
  private overlayEl: HTMLElement | null = null;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Host() private readonly datepicker: DatePicker,
    private readonly translate: TranslateService,
    private readonly zone: NgZone
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.datepicker.onShow.subscribe((overlay: HTMLElement) => this.onOverlayShown(overlay))
    );
    this.subscriptions.add(this.datepicker.onClose.subscribe(() => this.stopObserving()));
    // Header navigation (chevrons / month-year select) re-renders the grid.
    this.subscriptions.add(this.datepicker.onMonthChange.subscribe(() => this.scheduleRelabel()));
    this.subscriptions.add(this.datepicker.onYearChange.subscribe(() => this.scheduleRelabel()));
    // WCAG 2.1.1 / 2.4.3: in RANGE mode, selecting the first date re-renders the
    // day grid (PrimeNG rebuilds the cells to paint the in-range highlight), so
    // the cell that had keyboard focus is destroyed and focus falls back to
    // <body> — the user cannot pick the second date. Restore focus onto the
    // just-selected date's cell after the re-render so keyboard selection of the
    // range end can continue. Only acts while the panel is open.
    this.subscriptions.add(
      this.datepicker.onSelect.subscribe((selected: Date) => this.onDateSelected(selected))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopObserving();
  }

  private onOverlayShown(overlay: HTMLElement): void {
    this.overlayEl = overlay ?? (this.datepicker.overlay as HTMLElement) ?? null;
    if (!this.overlayEl) {
      return;
    }
    this.relabel();
    // WCAG 2.1.1 Keyboard: move focus into the calendar grid so arrow-key
    // navigation (which PrimeNG wires on each day span) works immediately.
    // PrimeNG suppresses this on trigger/button open via preventFocus.
    this.focusInitialDayCell();
    // Keyboard month navigation re-creates the day cells; observe so the new
    // cells are relabelled before the user hears them. Guard against
    // environments (SSR / some test runners) where MutationObserver is absent.
    if (typeof MutationObserver === "undefined") {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.relabel());
      this.observer.observe(this.overlayEl as HTMLElement, { childList: true, subtree: true });
    });
  }

  private stopObserving(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.overlayEl = null;
    if (this.focusTimer !== null) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }
  }

  private scheduleRelabel(): void {
    // Defer so PrimeNG has rendered the new month before we read the cells.
    this.zone.runOutsideAngular(() => setTimeout(() => this.relabel()));
  }

  /**
   * Moves keyboard focus onto the calendar's focusable day cell (selected day,
   * else today, else the first selectable day) so PrimeNG's per-cell arrow-key
   * navigation is available as soon as the panel opens. Deferred because
   * PrimeNG re-asserts `preventFocus` synchronously during its show cycle.
   *
   * When the panel is portaled out with `appendTo="body"` (and in range mode),
   * the day grid is not always attached/focusable by the first deferred turn, so
   * a single `setTimeout(0)` focus silently no-ops and focus stays on the
   * trigger — the reported "arrow keys do nothing". Retry a few animation frames
   * until a focusable day cell exists AND actually receives focus, then stop.
   * Never steals focus that is already inside the panel (e.g. the user tabbed to
   * the header chevrons, or PrimeNG placed it there itself).
   */
  private focusInitialDayCell(): void {
    this.zone.runOutsideAngular(() => {
      let attempts = 0;
      const maxAttempts = 10;
      const tryFocus = () => {
        this.focusTimer = null;
        const root = this.overlayEl ?? (this.datepicker.overlay as HTMLElement) ?? null;
        if (!root) {
          return;
        }
        const activeEl = root.ownerDocument?.activeElement ?? null;
        // Focus already inside the panel — leave it where the user/PrimeNG put it.
        if (activeEl && root.contains(activeEl)) {
          return;
        }
        const cell = this.findFocusableDayCell(root);
        if (cell) {
          cell.tabIndex = 0;
          cell.focus();
          // Confirm focus actually landed; if the grid was not yet focusable the
          // call is a no-op, so retry on the next frame.
          if (root.ownerDocument?.activeElement === cell) {
            return;
          }
        }
        if (++attempts < maxAttempts) {
          this.focusTimer = setTimeout(tryFocus);
        }
      };
      this.focusTimer = setTimeout(tryFocus);
    });
  }

  private findFocusableDayCell(root: HTMLElement): HTMLElement | null {
    for (const selector of DatepickerDayAriaLabelsDirective.FOCUSABLE_DAY_SELECTORS) {
      const cell = root.querySelector<HTMLElement>(selector);
      if (cell) {
        return cell;
      }
    }
    return null;
  }

  /**
   * After a date is selected, PrimeNG re-renders the day grid (especially in
   * range mode, to paint the in-range highlight), destroying the focused cell so
   * focus falls back to <body>. Re-focus the selected date's cell once the grid
   * has re-rendered so keyboard navigation/selection can continue (e.g. picking
   * the range end). No-op if the panel has closed (single-date select hides it).
   */
  private onDateSelected(selected: Date): void {
    if (!selected || !this.overlayEl) {
      return;
    }
    // PrimeNG's `data-date` marker is "YYYY-M-D" with a 0-based month.
    const dateKey = `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}`;
    this.zone.runOutsideAngular(() => {
      let attempts = 0;
      const maxAttempts = 10;
      const tryFocus = () => {
        this.focusTimer = null;
        const root = this.overlayEl ?? (this.datepicker.overlay as HTMLElement) ?? null;
        if (!root) {
          return;
        }
        const cell = root.querySelector<HTMLElement>(
          `span[data-date='${dateKey}']:not(.p-disabled):not(.p-ink)`
        );
        if (cell) {
          cell.tabIndex = 0;
          cell.focus();
          if (root.ownerDocument?.activeElement === cell) {
            return;
          }
        }
        if (++attempts < maxAttempts) {
          this.focusTimer = setTimeout(tryFocus);
        }
      };
      this.focusTimer = setTimeout(tryFocus);
    });
  }

  private relabel(): void {
    const root = this.overlayEl ?? (this.datepicker.overlay as HTMLElement) ?? null;
    if (!root) {
      return;
    }
    const days = root.querySelectorAll<HTMLElement>("span[data-date]");
    days.forEach((span) => {
      const label = this.buildLabel(span.getAttribute("data-date"));
      if (label) {
        span.setAttribute("aria-label", label);
      }
    });
  }

  /**
   * Turns PrimeNG's `data-date` marker ("YYYY-M-D", 0-based month) into a full
   * localized date string, or null when the marker is missing/malformed.
   */
  private buildLabel(dataDate: string | null): string | null {
    if (!dataDate) {
      return null;
    }
    const parts = dataDate.split("-").map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }
    const [year, monthIndex, day] = parts;
    const date = new Date(year, monthIndex, day);
    const locale = this.translate.currentLang || this.translate.defaultLang || "en";
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    };
    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return new Intl.DateTimeFormat("en", options).format(date);
    }
  }
}
